<template>
  <div class="store-order-details fade-in">
    <!-- Header Section -->
    <ProductDetailsHeader title="Store Purchase Order" :main="order" icon="bag-check">
      <template #header-actions>
        <select
            v-if="order.status !== 'complete' && orderProducts.data.length > 0"
            class="form-select w-auto"
            v-model="orderStatus"
            @change="confirmDialog(
                  getConfirmTitle,
                  updateOrderStatus,
                  'Are you sure you want to change order status to ' + orderStatus
                )" data="">
          <option value="" disabled selected>Change Status</option>
          <option v-if="order.status === 'pending'" value="submitted" class="text-primary fs-5">Submit Order</option>
          <option v-if="order.status === 'submitted'" value="pending" class="text-warning fs-5">Edit Order</option>
          <option v-if="order.status === 'submitted'" value="complete" class="text-success fs-5">Approve Order</option>
        </select>
      </template>
    </ProductDetailsHeader>

      <!-- Store Products -->
    <StoreProducts
        v-if="order.status === 'pending'"
        :per-page="5"
        :marked-products="orderProducts"
        @row-click="populateAddProduct"
    />
    <!-- End Store Products -->

    <!-- Order Products Section -->
    <ProductDetailsPages
        title="Purchase Order"
        :loading="loading"
        :detail-products="orderProducts"
        :current-page="detailsPage"
        :current-per-page="detailsPerPage"
        :current-search="detailsSearch"
        @update:page="onDetailsPageChange"
        @update:per-page="onDetailsPerPageChange"
        @update:search="onDetailsSearchChange"
        :main="order"
        @edit-product="populateOrderEdit"
        @delete-product="deleteOrderProduct"
    >
      <template #footer-actions>
        <div v-if="order.status !== 'complete' && orderProducts.data.length > 0">
          <select
              class="form-select status-select"
              v-model="orderStatus"
              @change="confirmDialog(
                  getConfirmTitle,
                  updateOrderStatus,
                  'Are you sure you want to change order status to ' + orderStatus
                )" data="">
            <option value="" disabled selected>Change Status</option>
            <option v-if="order.status === 'pending'" value="submitted" class="text-primary fs-5">Submit Order</option>
            <option v-if="order.status === 'submitted'" value="pending" class="text-warning fs-5">Edit Order</option>
            <option v-if="order.status === 'submitted'" value="complete" class="text-success fs-5">Approve Order</option>
          </select>
        </div>
      </template>
    </ProductDetailsPages>
    <!-- End Order Products Section -->

    <!-- Add/Edit Product Modal -->
    <Modal :open="addProductModal" @closed="addProductModal = false" dialog-size="modal-lg">
      <div class="modal-header">
        <h5 class="modal-title m-0" v-if="addProductMode === 'add'">
          <i class="bi bi-plus-circle me-2 text-primary"></i>Add Product to Purchase Order
        </h5>
        <h5 class="modal-title m-0" v-else>
          <i class="bi bi-pencil-square me-2 text-primary"></i>Edit Purchase Order Product
        </h5>
        <button @click="addProductModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <div class="row">
            <div class="form-group col-md-6">
              <label for="name" class="form-label fw-medium mb-2">Product Name:</label>
              <input type="text" class="form-control" :value="selectedProduct.name" disabled>
            </div>
            <div class="form-group col-md-6">
              <label for="quantity" class="form-label fw-medium mb-2">Quantity:</label>
              <input type="number" class="form-control" v-model="orderDetails.quantity" min="1">
            </div>
            <div class="form-group col-md-12 mt-3">
              <label for="supplier" class="form-label fw-medium mb-2">Supplier: <span class="text-danger">*</span></label>
              <select class="form-select" v-model="orderDetails.supplier_id" required data="">
                <option value="" disabled selected>Select supplier</option>
                <option v-for="supplier in suppliers" :key="supplier.id" :value="supplier.id">
                  {{ supplier.name }}
                </option>
              </select>
              <small class="form-text text-muted">Choose which supplier to order from</small>
            </div>
            <div class="form-group col-md-12 mt-3">
              <label for="comment" class="form-label fw-medium mb-2">Comment: <span class="text-muted fst-italic small">(optional)</span></label>
              <input type="text" class="form-control" v-model="orderDetails.comment" placeholder="Add any special instructions or notes">
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="addProductModal = false">Cancel</button>
        <button type="button" class="btn btn-primary" v-if="addProductMode === 'add'" @click="addOrderProduct">
          <i class="bi bi-plus-lg me-1"></i> Add Product
        </button>
        <button type="button" class="btn btn-primary" v-else @click="updateOrderProduct">
          <i class="bi bi-check-lg me-1"></i> Update Product
        </button>
      </div>
    </Modal>
  </div>
</template>

<script>
import storeOrderService from "@/api/storeOrderService";
import supplierService from "@/api/supplierService";
import formatDate from "@/utils/formatDate";
import confirmDialog  from "@/utils/confirmDialog";
import notify from "@/utils/notify";
import StoreProducts from "@/components/store/StoreProducts.vue";
import ProductDetailsPages from "@/components/details/ProductDetailsPages.vue";
import ProductDetailsHeader from "@/components/details/ProductDetailsHeader.vue";

export default {
  name: "OrderDetails",
  components: {ProductDetailsHeader, ProductDetailsPages, StoreProducts},

  data(){
    return {
      orderStatus: '',
      order: {
        status: '',
        store: {},
        user: {},
      },
      orderDetails: {
        store_order_id: this.$route.params.id,
        product_id: '',
        supplier_id: '',
        quantity: 1
      },
      selectedProduct: {
        name: '',
        supplier_id: ''
      },
      orderProducts: { data: [] },
      detailsPage: 1,
      detailsPerPage: 20,
      detailsSearch: '',
      suppliers: [],
      loading: true,
      addProductModal: false,
      addProductMode: 'add',
    }
  },

  methods:{
    formatDate,
    confirmDialog,
    async updateOrderStatus() {
      await storeOrderService.changeOrderStatus({status: this.orderStatus}, this.$route.params.id)
    },
    async getOrderDetails() {
      this.orderProducts = await storeOrderService.getOrderDetails(this.$route.params.id, {
        page: this.detailsPage,
        per_page: this.detailsPerPage
      })
      this.loading = false
    },
    onDetailsPageChange(page){
      this.detailsPage = page;
      this.getOrderDetails();
    },
    onDetailsPerPageChange(perPage){
      this.detailsPerPage = perPage;
      this.detailsPage = 1;
      this.getOrderDetails();
    },
    onDetailsSearchChange(search){
      this.detailsSearch = search;
      this.detailsPage = 1;
      this.getOrderDetails();
    },
    populateAddProduct(product) {
      this.addProductModal = true;
      this.selectedProduct.name = product.product.name
      this.selectedProduct.supplier_id = product.product.supplier_id
      this.orderDetails.product_id = product.product_id
      this.orderDetails.price = product.product.buy
      this.orderDetails.supplier_id = product.product.supplier_id || ''
    },
    async addOrderProduct() {
      let response = await storeOrderService.createOrderDetails(this.orderDetails)
      if (response) {
        this.addProductModal = false
        await this.getOrderDetails()
      }
    },
    async updateOrderProduct() {
      let response = await storeOrderService.updateOrderDetails(this.orderDetails, this.orderDetails.id)
      if (response) {
        this.addProductModal = false
        await this.getOrderDetails()
      }
    },
    populateOrderEdit(order) {
      this.selectedProduct.name = order.product.name
      this.selectedProduct.supplier_id = order.product.supplier_id
      this.orderDetails.id = order.id
      this.orderDetails.product_id = order.product_id
      this.orderDetails.price = order.product.buy
      this.orderDetails.quantity = order.quantity
      this.orderDetails.supplier_id = order.supplier_id || order.product.supplier_id || ''
      this.addProductModal = true;
      this.addProductMode = 'edit';
    },
    async deleteOrderProduct(id) {
      let response = await storeOrderService.deleteOrderDetails(id)
      if (response) {
        await this.getOrderDetails()
      }
    },
  },

  computed: {
    getConfirmTitle() {
      switch (this.orderStatus) {
        case 'submitted':
          return 'Submit Purchase Order'
        case 'pending':
          return 'Edit Purchase Order'
        case 'complete':
          return 'Approve Purchase Order'
      }
    }
  },

  created: async function () {
    this.order = await storeOrderService.getOrder(this.$route.params.id)
    const suppliersResponse = await supplierService.getSuppliers({ per_page: 500 })
    this.suppliers = suppliersResponse?.data || []
    await this.getOrderDetails()
  },

  watch: {
    addProductModal: function () {
      if (!this.addProductModal) {
        this.orderDetails = {
          store_order_id: this.$route.params.id,
          quantity: 1,
          supplier_id: ''
        }
        this.addProductMode = 'add'
      }
    }
  }

}
</script>

<style scoped>
</style>
